import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Chip,
  IconButton,
  InputAdornment,
  ClickAwayListener,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { navigate } from 'gatsby';
import {
  FaSearch,
  FaTimes,
  FaTools,
  FaPalette,
  FaCode,
  FaCalculator,
  FaImage,
  FaFileAlt,
  FaStar,
  FaPercent,
  FaRuler,
  FaShieldAlt,
  FaLock,
  FaExchangeAlt,
  FaQrcode,
  FaKey,
  FaFont,
  FaMagic,
  FaHashtag,
  FaVideo,
  FaEnvelope,
  FaExternalLinkAlt,
  FaArrowRight,
  FaBox,
  FaCalendarAlt,
} from 'react-icons/fa';

// Rich catalog of tools, calculators, and generators
const defaultToolsData = [
  // Developer Tools
  { id: 'json-formatter', name: 'JSON Formatter', path: '/tools/text/json-formatter', category: 'Developer Tools', icon: FaCode, color: '#38bdf8', keywords: ['json', 'format', 'validate', 'minify', 'pretty', 'code'], desc: 'Prettify, validate, and minify complex JSON data instantly.' },
  { id: 'base64-converter', name: 'Base64 Converter', path: '/tools/text/base64-converter', category: 'Developer Tools', icon: FaLock, color: '#818cf8', keywords: ['base64', 'encode', 'decode', 'converter', 'string'], desc: 'Encode and decode UTF-8 strings and media to Base64.' },
  { id: 'hash-generator', name: 'Hash Generator', path: '/tools/text/hash-generator', category: 'Developer Tools', icon: FaShieldAlt, color: '#a855f7', keywords: ['hash', 'md5', 'sha256', 'sha512', 'encrypt', 'security'], desc: 'Generate secure MD5, SHA-256, and SHA-512 cryptographic hashes.' },
  { id: 'regex-tester', name: 'Regex Tester', path: '/tools/text/regex-tester', category: 'Developer Tools', icon: FaCode, color: '#ec4899', keywords: ['regex', 'regular expression', 'pattern', 'test', 'match'], desc: 'Test and debug regular expressions with real-time matching highlights.' },
  { id: 'diff-checker', name: 'Diff Checker', path: '/tools/text/diff-checker', category: 'Developer Tools', icon: FaExchangeAlt, color: '#f43f5e', keywords: ['diff', 'compare', 'text', 'difference', 'code'], desc: 'Compare two text snippets or code blocks side-by-side with color diffs.' },
  { id: 'uuid-generator', name: 'UUID Generator', path: '/tools/text/uuid-generator', category: 'Developer Tools', icon: FaKey, color: '#fb923c', keywords: ['uuid', 'guid', 'generator', 'unique', 'random', 'v4'], desc: 'Generate bulk cryptographically random Version-4 UUIDs / GUIDs.' },

  // Design Tools
  { id: '3d-exhibition-stand', name: '3D Exhibition Stand Visualizer', path: '/tools/design/3d-exhibition-stand/', category: 'Design Tools', icon: FaBox, color: '#f38b22', keywords: ['exhibition', 'stand', 'booth', 'trade show', 'cosmoprof', 'stall', '3d', 'threejs', 'mockup', 'backdrop', 'expo', 'fascia', 'counter', 'display'], desc: 'Preview and configure your 4m × 3m trade show exhibition booth in real-time 3D with custom fabric and counter graphics.' },
  { id: 'design-distance-visualizer', name: 'Design Distance Visualizer (3D)', path: '/tools/design/design-distance-visualizer', category: 'Design Tools', icon: FaRuler, color: '#38bdf8', keywords: ['distance', 'visualizer', 'print', 'acuity', 'large format', 'billboard', 'ppi', 'dpi', '3d', 'threejs', 'banner', 'retina', 'optics'], desc: 'Simulate how large-format prints, billboards, and banners appear to the human eye from varying physical distances in 3D.' },
  { id: 'packaging-studio', name: 'Packaging Studio', path: '/tools/design/packaging-studio', category: 'Design Tools', icon: FaBox, color: '#6366f1', keywords: ['packaging', 'studio', '3d', 'mockup', 'box', 'dieline', 'product', 'design', 'render'], desc: 'Preview custom 3D boxes, pouches, and dielines in real-time before manufacturing.' },
  { id: 'color-scheme-tools', name: 'Color Scheme Tools', path: '/tools/design/color-scheme-tools', category: 'Design Tools', icon: FaPalette, color: '#ec4899', keywords: ['color', 'scheme', 'palette', 'explore', 'trending', 'harmonies'], desc: 'Curate trending brand color palettes and export production-ready design tokens.' },
  { id: 'color-palette-generator', name: 'Palette Generator', path: '/tools/design/color-palette-generator', category: 'Design Tools', icon: FaPalette, color: '#10b981', keywords: ['color', 'palette', 'harmony', 'design', 'hex', 'rgb'], desc: 'Generate harmonic color palettes with 1-click lock and CSS export.' },
  { id: 'gradient-generator', name: 'Gradient Generator', path: '/tools/generators/gradient-generator', category: 'Design Tools', icon: FaPalette, color: '#06b6d4', keywords: ['gradient', 'css', 'linear', 'radial', 'background'], desc: 'Design beautiful multi-stop linear and radial CSS gradients.' },
  { id: 'color-picker', name: 'Color Picker', path: '/tools/color-picker', category: 'Design Tools', icon: FaPalette, color: '#6366f1', keywords: ['color', 'picker', 'hex', 'rgb', 'hsl', 'eyedropper'], desc: 'Pick colors, convert between HEX, RGB, HSL, and inspect shades.' },
  { id: 'contrast-checker', name: 'Contrast Checker', path: '/tools/contrast-checker', category: 'Design Tools', icon: FaPalette, color: '#eab308', keywords: ['contrast', 'wcag', 'accessibility', 'colors', 'ratio'], desc: 'Ensure your typography passes WCAG AA/AAA standards for effortless readability.' },
  { id: 'palette-visualizer', name: 'Palette Visualizer', path: '/tools/palette-visualizer', category: 'Design Tools', icon: FaPalette, color: '#14b8a6', keywords: ['palette', 'visualizer', 'preview', 'theme', 'ui'], desc: 'Test your brand colors rendered live across real mobile and desktop UI screens.' },

  // Calculators
  { id: 'date-day-time-calculator', name: 'Date Calculator', path: '/tools/calculators/date-day-time-calculator', category: 'Calculators', icon: FaCalendarAlt, color: '#1565c0', keywords: ['date', 'day', 'time', 'duration', 'workdays', 'calendar', 'calculator'], desc: 'Calculate exact duration between dates, add or subtract days, and count workdays.' },
  { id: 'percentage-calculator', name: 'Percentage Calculator', path: '/tools/calculators/percentage-calculator', category: 'Calculators', icon: FaPercent, color: '#22c55e', keywords: ['percentage', 'percent', 'calculator', 'math', 'discount', 'ratio'], desc: 'Calculate percentage increases, discounts, fractional portions, and tips.' },
  { id: 'unit-converter', name: 'Unit Converter', path: '/tools/calculators/unit-converter', category: 'Calculators', icon: FaRuler, color: '#3b82f6', keywords: ['unit', 'convert', 'length', 'weight', 'temperature', 'speed', 'area'], desc: 'Convert length, weight, area, volume, and temperature effortlessly.' },
  { id: 'amazon-calculator', name: 'Amazon Fee Calculator', path: '/tools/marketplace/amazon-fee-calculator/', category: 'Calculators', icon: FaCalculator, brand: 'amazon', color: '#ff9900', keywords: ['amazon', 'fba', 'fee', 'profit', 'margin', 'seller'], desc: 'Audit exact referral fees, FBA pick & pack charges, and net bank deposits.' },
  { id: 'flipkart-calculator', name: 'Flipkart Profit Calc', path: '/tools/marketplace/flipkart-commission-calculator/', category: 'Calculators', icon: FaCalculator, brand: 'flipkart', color: '#2874f0', keywords: ['flipkart', 'fee', 'profit', 'marketplace', 'seller'], desc: 'Forecast category-wise Flipkart commissions, closing fees, and true profit margins.' },
  { id: 'meesho-calculator', name: 'Meesho Price Calculator', path: '/tools/marketplace/in/meesho-profit-calculator/', category: 'Calculators', icon: FaCalculator, brand: 'meesho', color: '#f43397', keywords: ['meesho', 'reseller', 'margin', 'pricing', 'calculator'], desc: 'Model zero-commission pricing, RTO courier returns, and net take-home earnings.' },
  { id: 'insta-engagement', name: 'Instagram Engagement', path: '/tools/instagram-engagement-rate-calculator', category: 'Calculators', icon: FaCalculator, color: '#d946ef', keywords: ['instagram', 'engagement', 'calculator', 'social', 'metrics'], desc: 'Calculate average engagement rate, likes ratio, and comment metrics.' },

  // Generators & Utilities
  { id: 'qr-code-generator', name: 'QR Code Generator', path: '/tools/generators/qr-code-generator', category: 'Generators', icon: FaQrcode, color: '#8b5cf6', keywords: ['qr', 'code', 'generator', 'barcode', 'scan', 'url'], desc: 'Generate high-resolution custom QR codes with color and download options.' },
  { id: 'lorem-ipsum-generator', name: 'Lorem Ipsum Generator', path: '/tools/generators/lorem-ipsum-generator', category: 'Generators', icon: FaFileAlt, color: '#64748b', keywords: ['lorem', 'ipsum', 'placeholder', 'text', 'dummy', 'filler'], desc: 'Produce dummy placeholder text paragraphs, sentences, and words.' },
  { id: 'favicon-generator', name: 'Favicon Generator', path: '/tools/generators/favicon-generator', category: 'Generators', icon: FaStar, color: '#f59e0b', keywords: ['favicon', 'icon', 'website', 'generator', 'png', 'ico'], desc: 'Generate multi-sized browser favicons and app icons in seconds.' },
  { id: 'meta-tag-generator', name: 'Meta Tag Generator', path: '/tools/meta-tag-generator', category: 'Generators', icon: FaMagic, color: '#06b6d4', keywords: ['meta', 'tags', 'seo', 'opengraph', 'twitter', 'social'], desc: 'Craft SEO and Open Graph social preview meta tags for websites.' },
  { id: 'schema-generator', name: 'Schema Generator', path: '/tools/schema-generator', category: 'Generators', icon: FaCode, color: '#10b981', keywords: ['schema', 'json-ld', 'seo', 'structured', 'google'], desc: 'Create rich JSON-LD structured data schema markup for search engines.' },
  { id: 'global-reel-inspiration', name: 'Global Reel Inspiration', path: '/tools/global-reel-inspiration/', category: 'Social Media', icon: FaVideo, color: '#ec4899', keywords: ['reel', 'inspiration', 'global', 'instagram', 'reference', 'discovery', 'industry', 'taxonomy', 'hooks', 'formats'], desc: 'Global industry-organized inspiration database and discovery engine for public Instagram Reels.' },
  { id: 'reel-script-studio', name: 'Reel Script Studio', path: '/tools/social-media/reel-script-studio/', category: 'Social Media', icon: FaVideo, color: '#38bdf8', keywords: ['reel', 'script', 'studio', 'tiktok', 'shorts', 'youtube', 'hooks', 'frameworks', 'story curve', 'video', 'creator'], desc: 'Visual timeline short-form video script editor with 5,000 hooks, 1,000 frameworks & story curve.' },
  { id: 'instagram-caption', name: 'Caption Generator', path: '/tools/instagram-caption-generator', category: 'Generators', icon: FaHashtag, color: '#ec4899', keywords: ['instagram', 'caption', 'generator', 'hashtag', 'post'], desc: 'AI-assisted engaging captions and hashtag sets for Instagram posts.' },
  { id: 'email-extractor', name: 'Email Extractor', path: '/tools/email-extractor', category: 'Utilities', icon: FaEnvelope, color: '#0ea5e9', keywords: ['email', 'extractor', 'scrape', 'text', 'regex'], desc: 'Extract, clean, and export verified founder & client email lists from raw text.' },
  { id: 'image-picker', name: 'Image Color Picker', path: '/tools/image-picker', category: 'Design Tools', icon: FaImage, color: '#6366f1', keywords: ['image', 'picker', 'convert', 'palette', 'colors'], desc: 'Extract dominant palettes and inspect assets directly from images.' },
];

// Helper brand logo renderer
const BrandLogo = ({ brand, size = 24 }) => {
  if (!brand) return null;
  const common = { width: size, height: size, style: { display: 'block' } };
  switch (brand) {
    case 'amazon':
      return (
        <svg {...common} viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <path d="M50 220c40 60 160 120 300 30" fill="none" stroke="#FF9900" strokeWidth="34" strokeLinecap="round" />
        </svg>
      );
    case 'flipkart':
      return (
        <svg {...common} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <rect width="24" height="24" rx="4" fill="#2874F0" />
          <path d="M6 7h12v10H6z" fill="#fff" opacity="0.02" />
        </svg>
      );
    case 'meesho':
      return (
        <svg {...common} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <rect width="24" height="24" rx="4" fill="#F43397" />
        </svg>
      );
    default:
      return null;
  }
};

// Generate deterministic ambient 3D orbit positions and flight paths for floating cards
const generateAmbientCoords = (index, total = 10) => {
  const angle = (index / total) * Math.PI * 2 - Math.PI / 2;
  // Elliptical distribution: wide horizontal spread (36-42vw), vertical spread (26-32vh)
  const radiusX = 36 + (index % 3) * 3;
  const radiusY = 27 + (index % 2) * 4;
  const zTier = (index % 2 === 0 ? 15 : -30);

  let posX = Math.cos(angle) * radiusX;
  let posY = Math.sin(angle) * radiusY;

  // Make sure not directly centered over the search bar
  if (Math.abs(posX) < 20 && Math.abs(posY) < 16) {
    posX = posX >= 0 ? posX + 18 : posX - 18;
    posY = posY >= 0 ? posY + 14 : posY - 14;
  }

  // 3D rotations for organic floating
  const rotZ = ((index * 7) % 14) - 7; // -7deg to +7deg
  const rotX = ((index * 9) % 12) - 6;
  const rotY = ((index * 11) % 14) - 7;

  // Asynchronous flight trajectories
  const driftX = 12 + (index % 4) * 4; // 12px to 24px
  const driftY = 14 + (index % 3) * 5; // 14px to 24px
  const duration = 5.2 + (index % 5) * 0.8; // 5.2s to 8.4s
  const delay = (index % 6) * 0.35;

  return {
    posX,
    posY,
    zTier,
    rotZ,
    rotX,
    rotY,
    driftX,
    driftY,
    duration,
    delay,
    orbitTier: index % 2,
  };
};

function SearchBar({ variant = 'default', siteIndex = [], onClose }) {
  const theme = useTheme();
  const prefersReducedMotion = useMediaQuery('@media (prefers-reduced-motion: reduce)');
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredCardId, setHoveredCardId] = useState(null);
  const searchRef = useRef(null);
  const inputRef = useRef(null);

  const isGradient = variant === 'gradient';

  // Lock page scroll when overlay mode is active
  useEffect(() => {
    if (!isGradient) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('search-active');
    return () => {
      document.body.style.overflow = prev;
      document.body.classList.remove('search-active');
    };
  }, [isGradient]);

  // Focus input automatically when overlay opens
  useEffect(() => {
    if (isGradient && inputRef.current) {
      const t = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(t);
    }
  }, [isGradient]);

  // Combine default tools with any siteIndex props for full database searching
  const allCards = useMemo(() => {
    const list = [...defaultToolsData];
    if (Array.isArray(siteIndex) && siteIndex.length > 0) {
      siteIndex.forEach((item, idx) => {
        if (!list.some((t) => t.path === item.path)) {
          list.push({
            id: `site-${idx}`,
            name: item.title || item.name || 'Tool',
            path: item.path,
            category: item.category || 'Platform',
            icon: FaTools,
            color: '#6366f1',
            keywords: [(item.title || '').toLowerCase(), (item.category || '').toLowerCase()],
            desc: item.desc || 'Explore this resource and utility.',
            brand: item.brand || null,
          });
        }
      });
    }
    return list;
  }, [siteIndex]);

  // Ambient tools (strictly capped to 10 for buttery smooth, zero-crash performance)
  const MAX_AMBIENT_CARDS = 10;
  const ambientCards = useMemo(() => {
    return defaultToolsData.slice(0, MAX_AMBIENT_CARDS).map((card, idx) => ({
      ...card,
      coords: generateAmbientCoords(idx, MAX_AMBIENT_CARDS),
    }));
  }, []);

  // Filter matching cards based on query across the FULL catalog
  const searchTerms = useMemo(() => {
    return query.toLowerCase().split(/\s+/).filter(Boolean);
  }, [query]);

  const isSearching = searchTerms.length > 0;

  const matches = useMemo(() => {
    if (!isSearching) return [];
    return allCards.filter((card) => {
      const textToSearch = `${card.name} ${card.category} ${card.desc} ${(card.keywords || []).join(' ')}`.toLowerCase();
      return searchTerms.every((term) => textToSearch.includes(term));
    });
  }, [allCards, searchTerms, isSearching]);

  // Cap visible match cards in 3D to at most 6 for clean layout and peak GPU speed
  const MAX_MATCH_CARDS = 6;
  const visibleMatches = useMemo(() => {
    return matches.slice(0, MAX_MATCH_CARDS);
  }, [matches]);

  // Active display cards for 3D canvas (capped at max 16 cards for peak 60-120fps performance)
  const displayCards = useMemo(() => {
    if (!isSearching) {
      return ambientCards.map((card) => ({
        ...card,
        isMatch: false,
      }));
    }

    const matchIds = new Set(visibleMatches.map((m) => m.id));
    const matchCards = visibleMatches.map((card, idx) => ({
      ...card,
      coords: card.coords || generateAmbientCoords(idx, 6),
      isMatch: true,
      matchIndex: idx,
      totalMatches: visibleMatches.length,
    }));

    const backgroundCards = ambientCards
      .filter((card) => !matchIds.has(card.id))
      .map((card) => ({
        ...card,
        isMatch: false,
      }));

    return [...matchCards, ...backgroundCards];
  }, [isSearching, visibleMatches, ambientCards]);

  // Handle result selection
  const handleResultClick = (tool) => {
    if (onClose) onClose();
    handleClose();
    navigate(tool.path);
  };

  // Close search
  const handleClose = () => {
    setIsOpen(false);
    setQuery('');
    if (onClose) onClose();
  };

  // Clear search
  const handleClear = () => {
    setQuery('');
    inputRef.current?.focus();
  };

  // Handle keyboard navigation
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      handleClose();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (matches.length > 0) {
        handleResultClick(matches[0]);
      }
    }
  };

  // ==========================================
  // GRADIENT / 3D FLYING CARDS OVERLAY MODE
  // ==========================================
  if (isGradient) {
    return (
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          perspective: '1300px',
          transformStyle: 'preserve-3d',
        }}
      >
        {/* Background Ambient Glow Lights */}
        <Box
          sx={{
            position: 'absolute',
            width: '600px',
            height: '600px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, rgba(56, 189, 248, 0.05) 50%, transparent 70%)',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            zIndex: 1,
            filter: 'blur(40px)',
          }}
        />

        {/* 3D SPATIAL FLYING CARDS CANVAS */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            transformStyle: 'preserve-3d',
            pointerEvents: 'none',
            zIndex: 2,
          }}
        >
          {displayCards.map((card) => {
            const isHovered = hoveredCardId === card.id;

            let targetX, targetY, targetZ, targetRotX, targetRotY, targetRotZ, targetScale, targetOpacity, zIndex;

            if (card.isMatch) {
              // Matching cards swooped right into user's hand (max 6 cards)
              const matchIndex = card.matchIndex;
              const totalMatches = card.totalMatches;

              if (totalMatches === 1) {
                targetX = 0;
                targetY = 145;
              } else if (totalMatches === 2) {
                targetX = matchIndex === 0 ? -145 : 145;
                targetY = 145;
              } else if (totalMatches === 3) {
                targetX = (matchIndex - 1) * 270;
                targetY = 145;
              } else {
                const row = Math.floor(matchIndex / 3);
                const col = matchIndex % 3;
                const countInRow = row === 0 ? 3 : totalMatches - 3;
                targetX = (col - (countInRow - 1) / 2) * 270;
                targetY = 130 + row * 145;
              }

              targetZ = isHovered ? 170 : 130;
              targetRotX = 0;
              targetRotY = 0;
              targetRotZ = totalMatches > 1 ? (matchIndex - (totalMatches - 1) / 2) * 2 : 0;
              targetScale = isHovered ? 1.12 : 1.05;
              targetOpacity = 1;
              zIndex = 200 + matchIndex;
            } else if (isSearching) {
              // Background ambient cards when searching: disperse backward into deep space like letters fluttering away
              targetX = `${card.coords.posX * 1.2}vw`;
              targetY = `${card.coords.posY * 1.2}vh`;
              targetZ = card.coords.zTier - 360;
              targetRotX = card.coords.rotX;
              targetRotY = card.coords.rotY;
              targetRotZ = card.coords.rotZ;
              targetScale = 0.55;
              targetOpacity = 0.12;
              zIndex = 10;
            } else {
              // Ambient floating cards idle mode (strictly curated tools in full 3D flight)
              targetX = `${card.coords.posX}vw`;
              targetY = `${card.coords.posY}vh`;
              targetZ = isHovered ? card.coords.zTier + 55 : card.coords.zTier;
              targetRotX = card.coords.rotX;
              targetRotY = card.coords.rotY;
              targetRotZ = card.coords.rotZ;
              targetScale = isHovered ? 1.08 : 0.95;
              targetOpacity = isHovered ? 1 : 0.88;
              zIndex = isHovered ? 160 : 25;
            }

            const IconComp = card.icon || FaTools;

            return (
              <motion.div
                key={card.id}
                initial={
                  card.isMatch
                    ? {
                        opacity: 0,
                        scale: 0.15,
                        z: -600,
                        rotateX: 45,
                        rotateZ: -25,
                      }
                    : {
                        opacity: 0,
                        scale: 0.8,
                      }
                }
                animate={{
                  x: typeof targetX === 'string' ? targetX : targetX,
                  y: typeof targetY === 'string' ? targetY : targetY,
                  z: targetZ,
                  rotateX: targetRotX,
                  rotateY: targetRotY,
                  rotateZ: targetRotZ,
                  scale: targetScale,
                  opacity: targetOpacity,
                }}
                transition={{
                  type: 'spring',
                  stiffness: card.isMatch ? 220 : 90,
                  damping: card.isMatch ? 20 : 18,
                  mass: card.isMatch ? 0.8 : 1,
                }}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: -60,
                  marginLeft: -125,
                  width: 250,
                  zIndex: isHovered ? 260 : zIndex,
                  pointerEvents: card.isMatch || !isSearching ? 'auto' : 'none',
                  transformStyle: 'preserve-3d',
                }}
              >
                {/* 3D Multi-axis Floating Drift & Hover Animation */}
                <motion.div
                  animate={
                    prefersReducedMotion ? {} :
                    card.isMatch
                      ? {
                          // Gentle magical levitation in hand like a caught Harry Potter letter
                          y: [0, -6, 0, 4, 0],
                          rotateZ: [0, 1.2, 0, -1.2, 0],
                        }
                      : !isSearching && card.coords
                      ? {
                          // Organic continuous 3D multi-axis flight trajectory in mid-air
                          x: [0, card.coords.driftX, 0, -card.coords.driftX * 0.85, 0],
                          y: [0, -card.coords.driftY, 0, card.coords.driftY * 0.75, 0],
                          rotateX: [0, 6, 0, -5, 0],
                          rotateY: [0, -7, 0, 7, 0],
                          rotateZ: [0, 4, 0, -4, 0],
                        }
                      : {
                          y: [0, -3, 0],
                        }
                  }
                  transition={{
                    duration: card.isMatch ? 3.6 : card.coords ? card.coords.duration : 5.5,
                    delay: card.isMatch ? 0 : card.coords ? card.coords.delay : 0,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                  onMouseEnter={() => setHoveredCardId(card.id)}
                  onMouseLeave={() => setHoveredCardId(null)}
                  onClick={() => handleResultClick(card)}
                  style={{
                    cursor: 'pointer',
                  }}
                >
                  <Paper
                    elevation={card.isMatch ? 16 : 4}
                    sx={{
                      p: 1.8,
                      borderRadius: '18px',
                      background: card.isMatch
                        ? 'linear-gradient(135deg, rgba(28, 33, 56, 0.97) 0%, rgba(15, 20, 36, 0.98) 100%)'
                        : 'linear-gradient(135deg, rgba(25, 33, 50, 0.85) 0%, rgba(15, 23, 42, 0.90) 100%)',
                      backdropFilter: 'blur(16px)',
                      border: card.isMatch
                        ? '2px solid rgba(251, 191, 36, 0.95)'
                        : isHovered
                        ? '1.5px solid rgba(255, 255, 255, 0.4)'
                        : '1px solid rgba(255, 255, 255, 0.14)',
                      boxShadow: card.isMatch
                        ? '0 24px 60px rgba(0, 0, 0, 0.85), 0 0 38px rgba(245, 158, 11, 0.45), inset 0 1px 2px rgba(255, 255, 255, 0.3)'
                        : isHovered
                        ? '0 14px 30px rgba(0, 0, 0, 0.5), 0 0 20px rgba(255, 255, 255, 0.2)'
                        : '0 8px 24px rgba(0, 0, 0, 0.35)',
                      color: '#ffffff',
                      userSelect: 'none',
                      transition: 'border 0.2s ease, box-shadow 0.2s ease',
                    }}
                  >
                    {/* Card Top: Icon & Category / Caught Badge */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.2 }}>
                      <Box
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: card.isMatch
                            ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.3), rgba(217, 119, 6, 0.15))'
                            : `linear-gradient(135deg, ${card.color || '#6366f1'}33, ${card.color || '#6366f1'}15)`,
                          border: card.isMatch
                            ? '1.5px solid rgba(251, 191, 36, 0.6)'
                            : `1px solid ${card.color || '#6366f1'}55`,
                          color: card.isMatch ? '#fbbf24' : card.color || '#818cf8',
                        }}
                      >
                        {card.brand ? <BrandLogo brand={card.brand} size={22} /> : <IconComp size={18} />}
                      </Box>

                      {card.isMatch ? (
                        <Chip
                          icon={<FaEnvelope size={10} style={{ color: '#fbbf24' }} />}
                          label={card.matchIndex === 0 ? 'CAUGHT IN FLIGHT' : card.category}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: '0.66rem',
                            fontWeight: 800,
                            letterSpacing: '0.4px',
                            textTransform: 'uppercase',
                            bgcolor: 'rgba(245, 158, 11, 0.22)',
                            color: '#fde68a',
                            border: '1px solid rgba(251, 191, 36, 0.6)',
                            boxShadow: '0 0 10px rgba(245, 158, 11, 0.3)',
                            '& .MuiChip-icon': { ml: 0.6, mr: -0.2 },
                          }}
                        />
                      ) : (
                        <Chip
                          label={card.category}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            letterSpacing: '0.3px',
                            textTransform: 'uppercase',
                            bgcolor: 'rgba(255, 255, 255, 0.08)',
                            color: 'rgba(255, 255, 255, 0.75)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                          }}
                        />
                      )}
                    </Box>

                    {/* Card Title */}
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 800,
                        fontSize: '0.92rem',
                        lineHeight: 1.3,
                        mb: 0.5,
                        color: card.isMatch ? '#ffffff' : '#f1f5f9',
                      }}
                    >
                      {card.name}
                    </Typography>

                    {/* Card Description */}
                    <Typography
                      variant="caption"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        fontSize: '0.74rem',
                        lineHeight: 1.35,
                        color: 'rgba(255, 255, 255, 0.65)',
                      }}
                    >
                      {card.desc}
                    </Typography>

                    {/* Bottom CTA when in front */}
                    {card.isMatch && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 0.8,
                          mt: 1.2,
                          pt: 0.8,
                          borderTop: '1px solid rgba(251, 191, 36, 0.25)',
                          fontSize: '0.74rem',
                          fontWeight: 700,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            color: '#fbbf24',
                            fontSize: '0.68rem',
                            bgcolor: 'rgba(245, 158, 11, 0.15)',
                            px: 0.8,
                            py: 0.2,
                            borderRadius: '4px',
                            border: '1px solid rgba(245, 158, 11, 0.3)',
                          }}
                        >
                          <span>ENTER ⏎</span>
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.6,
                            color: '#fde68a',
                            fontWeight: 700,
                          }}
                        >
                          <span>Open Tool</span>
                          <FaArrowRight size={11} />
                        </Box>
                      </Box>
                    )}
                  </Paper>
                </motion.div>
              </motion.div>
            );
          })}
        </Box>

        {/* CENTER ALIGNED SEARCH CONTAINER */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 150,
            width: '100%',
            maxWidth: { xs: '90vw', sm: 600, md: 700 },
            px: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            transform: isSearching && matches.length > 0 ? 'translateY(-120px)' : 'translateY(0px)',
            transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          {/* Main Rounded Input Box */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'rgba(13, 20, 36, 0.88)',
              backdropFilter: 'blur(28px)',
              borderRadius: '9999px', // Pill-shaped rounded corners as requested
              border: '1.5px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.65), 0 0 35px rgba(99, 102, 241, 0.25)',
              p: '6px 12px 6px 20px',
              transition: 'all 0.3s ease',
              '&:hover': {
                border: '1.5px solid rgba(129, 140, 248, 0.6)',
                boxShadow: '0 25px 60px rgba(0, 0, 0, 0.75), 0 0 45px rgba(99, 102, 241, 0.35)',
              },
              '&:focus-within': {
                border: '1.5px solid rgba(129, 140, 248, 0.95)',
                boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.35), 0 25px 60px rgba(0, 0, 0, 0.8), 0 0 50px rgba(99, 102, 241, 0.45)',
              },
            }}
          >
            {/* Search Icon */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.3), rgba(56, 189, 248, 0.2))',
                color: '#38bdf8',
                mr: 1.5,
                flexShrink: 0,
              }}
            >
              <FaSearch size={16} />
            </Box>

            {/* Input field */}
            <TextField
              inputRef={inputRef}
              autoFocus
              fullWidth
              variant="standard"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search tools, calculators, generators..."
              InputProps={{
                disableUnderline: true,
                style: {
                  fontSize: '1.15rem',
                  fontWeight: 500,
                  color: '#ffffff',
                },
              }}
              sx={{
                '& input::placeholder': {
                  color: 'rgba(255, 255, 255, 0.55)',
                  opacity: 1,
                },
              }}
            />

            {/* Clear Button */}
            {query && (
              <IconButton
                size="small"
                onClick={handleClear}
                aria-label="Clear query"
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  bgcolor: 'rgba(255, 255, 255, 0.08)',
                  mr: 1,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.18)',
                    color: '#ffffff',
                  },
                }}
              >
                <FaTimes size={13} />
              </IconButton>
            )}

            {/* ESC Badge */}
            <Box
              sx={{
                px: 1.2,
                py: 0.5,
                borderRadius: '8px',
                bgcolor: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '0.72rem',
                fontWeight: 700,
                letterSpacing: '0.5px',
                userSelect: 'none',
                flexShrink: 0,
              }}
            >
              ESC
            </Box>
          </Box>

          {/* Subtitle / Status Hint */}
          <Typography
            variant="caption"
            sx={{
              mt: 1.8,
              color: 'rgba(255, 255, 255, 0.55)',
              fontSize: '0.8rem',
              fontWeight: 500,
              letterSpacing: '0.2px',
              textAlign: 'center',
              userSelect: 'none',
            }}
          >
            {isSearching ? (
              matches.length > 0 ? (
                <span>
                  ✦ Caught in flight! Press <strong style={{ color: '#fbbf24' }}>Enter ⏎</strong> or click to open • Found <strong style={{ color: '#38bdf8' }}>{matches.length}</strong> matching {matches.length === 1 ? 'tool' : 'tools'}{matches.length > 6 ? ' (showing top 6)' : ''}
                </span>
              ) : (
                <span style={{ color: '#f87171' }}>No tools matched "{query}" — Try searching for "calc", "palette", "qr", or "json"</span>
              )
            ) : (
              <span>
                ✦ 3D Flying Cards in Motion • Type any tool name to call it into your hand
              </span>
            )}
          </Typography>
        </Box>
      </Box>
    );
  }

  // ==========================================
  // DEFAULT / INLINE NAVBAR SEARCH FALLBACK
  // ==========================================
  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box
        ref={searchRef}
        sx={{
          position: 'relative',
          width: { xs: '100%', sm: 300, md: 400 },
          maxWidth: 400,
        }}
      >
        <TextField
          ref={inputRef}
          fullWidth
          size="small"
          placeholder="Search tools..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaSearch size={16} style={{ opacity: 0.5 }} />
              </InputAdornment>
            ),
            endAdornment: query && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClear}>
                  <FaTimes size={14} />
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              bgcolor: 'background.paper',
              borderRadius: '9999px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'divider',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
            },
          }}
          sx={{
            '& .MuiInputBase-root': {
              backgroundColor: theme.palette.background.paper,
              borderRadius: '9999px',
              '& input': {
                color: theme.palette.text.primary,
                '&::placeholder': {
                  color: theme.palette.text.secondary,
                },
              },
            },
          }}
        />

        {isOpen && matches.length > 0 && (
          <Paper
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              mt: 1,
              maxHeight: 400,
              overflow: 'auto',
              zIndex: 1300,
              boxShadow: theme.shadows[8],
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
            }}
          >
            <List disablePadding>
              {matches.map((item) => (
                <ListItem
                  key={item.id}
                  button
                  onClick={() => handleResultClick(item)}
                  sx={{
                    py: 1.5,
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 44 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: `${item.color || '#6366f1'}22`,
                        color: item.color || '#6366f1',
                      }}
                    >
                      {item.brand ? <BrandLogo brand={item.brand} size={20} /> : (item.icon ? <item.icon size={16} /> : <FaTools size={16} />)}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {item.name}
                        </Typography>
                        <Chip label={item.category} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {(item.desc || '').slice(0, 75)}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
}

export default SearchBar;
